import { BaseEntity } from './BaseEntity';
import { Country } from './Country';
import { Language } from './Language';

/**
 * The survey for one class: what the survey platform scored the class at, and what each student
 * answered.
 *
 * Read only, and there is no write endpoint to add here later - `SurveyResource` on the service says
 * so explicitly. Surveys arrive on a topic, upstream owns them, and an editable survey would be a
 * second answer that the next message overwrote.
 *
 * The service splits the data in two, and so does everything below:
 *
 * - {@link SurveyView} - the six scores and the comments with the respondents taken off. Any signed
 *   in user may read it.
 * - {@link Submission} - one student's answers, joined to who they are. **`admin` only**, because
 *   that is where the name, the email and the employer are.
 *
 * A page that shows both has to be able to show the first without the second: see
 * `ClassSurvey.tsx`, which does exactly that for a reader who is not an administrator.
 */

/**
 * One comment box's text, and the English of it the service holds.
 *
 * The translation is the service's, stored beside the original rather than fetched by a reader -
 * a class's comments are translated once, by the "Translate comments" action on the survey page,
 * and everybody who reads them afterwards reads the same English. Nothing in this application
 * translates anything.
 *
 * Three states, and they are told apart by comparing the two fields rather than by a flag:
 *
 * - `english` absent - nobody has run the translation over this class yet.
 * - `english` equal to `original` - the student wrote in English, so there is no second version
 *   and nothing to switch to. See {@link isAlreadyEnglish}.
 * - `english` different - there are two versions. See {@link hasTranslation}.
 */
export interface TranslatedComment {
  /** What the student wrote, in whatever language they wrote it. Always the record. */
  original: string;
  /** The English the service stored, or absent until the class has been translated. */
  english?: string | null;
}

/**
 * The five comment boxes, each as the list of what was written in it.
 *
 * Grouped by subject rather than by author, which is what makes them readable without `admin`: the
 * lists are independent, so nothing lines a curriculum comment up with the instructor comment from
 * the same student. A box left blank contributes nothing, so the lists are of different lengths and
 * an index into one means nothing in another.
 */
export interface SurveyComments {
  overall: TranslatedComment[];
  instructor: TranslatedComment[];
  facility: TranslatedComment[];
  curriculum: TranslatedComment[];
  bookshelf: TranslatedComment[];
}

/**
 * What translating a class's comments did, as the service reports it.
 *
 * Worth showing rather than swallowing: "translated" and "already in English" are both successes
 * and mean completely different things to whoever pressed the option, and a class that comes back
 * entirely `alreadyInEnglish` explains why nothing on the page changed.
 */
export interface TranslationSummary {
  klassId: number;
  surveyId: number;
  /** Comments looked at. */
  comments: number;
  /** Comments this run put an English version on. */
  translated: number;
  /** Comments the student had already written in English. */
  alreadyInEnglish: number;
  /** Comments left alone because they already carried a translation - see the force option. */
  skipped: number;
  failed: number;
}

/** A survey as anybody signed in may read it. */
export interface SurveyView {
  id: number;
  /** The class's own id - what `/survey/class/{klassId}` takes. Not the offering id. */
  klassId: number;
  offeringId: number;
  courseSku?: string;
  courseName?: string;

  /**
   * The six scores, **as the survey platform computed them**, on the same 1-5 scale as the
   * questions - except {@link nps}, which is a Net Promoter Score and runs -100 to 100.
   *
   * Reported, never recalculated. Which questions feed which score, how a partial submission is
   * weighted and what an unanswered question counts as are all upstream's to define, and averaging
   * the submissions here would invent a second definition that disagreed with upstream's own
   * reports. Where this application does average the answers it has - the per-question column in the
   * matrix - it is labelled as computed, and it is normal for it not to equal the score beside it.
   */
  instructorScore?: number;
  facilityScore?: number;
  curriculumScore?: number;
  labScore?: number;
  /** The class's Net Promoter Score, -100 to 100. Not {@link Submission.nps}, which is one answer. */
  nps?: number;
  overallScore?: number;

  /** How many submissions the service holds, which is not necessarily upstream's own count. */
  nrSubmissions: number;
  comments: SurveyComments;
}

/**
 * One student's response.
 *
 * Carries the respondent - {@link name}, {@link email}, {@link company}, {@link jobTitle},
 * {@link username} - which is why the endpoint that answers these needs `admin`. Nothing in this
 * application may show one to a reader who does not hold it.
 *
 * Facts about the class are deliberately absent: the offering id, the course, the instructor, the
 * modality and the region all arrive on the submission upstream and are all answered by the class
 * the survey hangs off. {@link language} is the exception, and is the language the *student* chose
 * to answer in rather than the one the class was taught in.
 */
export interface Submission extends BaseEntity {
  /** Upstream's identifier, unique across every survey. The key a re-sent submission is matched on. */
  uuid: string;

  createTime?: string;
  updateTime?: string;
  startDate?: string;
  /** The date the submission counts as, which is what a survey is reported by. */
  surveyDate?: string;

  surveySlug?: string;
  /** The version of the survey form. Not the course version. */
  surveyVersion?: string;
  /** How far through the form the student got, in upstream's own vocabulary. */
  progress?: string;
  /** The same thing as a fraction, as upstream computed it. 0 to 1. */
  completionRatio?: number;

  name?: string;
  email?: string;
  company?: string;
  jobTitle?: string;
  companyType?: string;
  companyRelationship?: string;
  username?: string;
  /** How the student was entitled to the class - a subscription, a voucher, a direct purchase. */
  entitlementType?: string;
  /** The respondent's country, which is not necessarily the class's. Null when it matched nothing. */
  country?: Country;
  /** The language the student answered in. Null when the code upstream sent matched nothing. */
  language?: Language;

  didSkillsAssessment?: boolean;
  metPreRequisites?: boolean;
  betterPrepared?: boolean;
  testedConnection?: boolean;
  receivedLogin?: boolean;
  exploreAdopt?: boolean;
  projectSucceed?: boolean;
  didContactSupport?: boolean;
  increaseSupportKnowledge?: boolean;
  recommendExpand?: boolean;
  recommendPurchase?: boolean;
  recommendRenewal?: boolean;

  /** This respondent's own Net Promoter answer, 0 to 10. Not {@link SurveyView.nps}. */
  nps?: number;
  overallRate?: number;

  instructorExpertise?: number;
  instructorPrepared?: number;
  instructorClarity?: number;
  instructorAnswers?: number;
  instructorManage?: number;
  instructorWebManage?: number;

  coursePace?: number;
  courseRelevant?: number;
  studentGuideAccurate?: number;
  courseWebApp?: number;
  videosWorthwhile?: number;
  videoQuality?: number;
  bookshelfExperience?: number;

  labsRelevant?: number;
  labPerformance?: number;

  roomConfortable?: number;
  hardware?: number;
  facilityStaff?: number;
  conferenceAudio?: number;
  conferenceWebApp?: number;

  supportPre?: number;
  supportResponsive?: number;

  whyHere?: string;
  howLongPreviousCourse?: string;
  whyContactSupport?: string;
  expectTrainingNeeds?: string;
  expectTrainingConsume?: string;

  commentOverall?: string;
  commentInstructor?: string;
  commentFacilities?: string;
  commentCurriculum?: string;
  commentFeaturesBookshelf?: string;

  /**
   * The English of each box, as the service stored it. Paired with the field above it by
   * {@link SURVEY_COMMENT_GROUPS}, and read through {@link submissionComment} rather than by
   * naming both halves at a call site.
   */
  commentOverallEnglish?: string;
  commentInstructorEnglish?: string;
  commentFacilitiesEnglish?: string;
  commentCurriculumEnglish?: string;
  commentFeaturesBookshelfEnglish?: string;
}

// ---------------------------------------------------------------------------------------------
// The questions
// ---------------------------------------------------------------------------------------------

/**
 * What kind of answer a question takes, which decides how it is scored and how a cell is drawn.
 *
 * `rating` and `nps` are both numbers and are deliberately not one kind: they are on different
 * scales and, more importantly, a 7 is a good rating and a middling Net Promoter answer. Everything
 * that colours or averages an answer asks the kind first - see {@link ratingScale} and
 * {@link answerTone} - so nothing has to remember which fields are the exception.
 */
export type QuestionKind = 'rating' | 'nps' | 'yesno';

export interface SurveyQuestion {
  /** The field on {@link Submission} this reads. */
  field: keyof Submission;
  /** @see SURVEY_SECTIONS for how provisional this wording is. */
  label: string;
  kind: QuestionKind;
  /**
   * Why the question is asked, when the field name alone does not say. Shown on the question's
   * tooltip beside the raw field name.
   */
  hint?: string;
}

export interface SurveySection {
  key: string;
  title: string;
  /**
   * The score upstream computes for this section, when it computes one. Four of the six scores map
   * to a section; `overallScore` and `nps` are the class as a whole and live on the Overall section.
   */
  score?: keyof SurveyView;
  questions: SurveyQuestion[];
}

/**
 * Every question, grouped the way the scores group them.
 *
 * **THE WORDING IS PROVISIONAL.** Upstream sends field names and nothing else - `q_instr_expertise`,
 * `q_room_comfortable` - and the two mapping spreadsheets that exist map those names to columns
 * without ever saying what the student was asked. These labels are read off the field names and are
 * a guess at the question, which is why every one of them shows its raw field name on a tooltip: a
 * reader who knows the form can see what a label really refers to, and correcting a label here is
 * then a one line change. When the document describing the questions turns up, this is the only
 * place that has to change.
 *
 * The order is the order the matrix and the distribution both draw, so a question moved here moves
 * on screen.
 *
 * `instructorManage` and `instructorWebManage` are the same question asked of a classroom and of a
 * virtual classroom. Both arrive on every submission and which one is answered depends on how the
 * class was delivered, so a whole column of blanks against one of them is the normal case and not a
 * gap in the data.
 */
export const SURVEY_SECTIONS: readonly SurveySection[] = [
  {
    key: 'overall',
    title: 'Overall',
    score: 'overallScore',
    questions: [
      { field: 'overallRate', label: 'Overall rating of the course', kind: 'rating' },
      {
        field: 'nps',
        label: 'Would recommend (NPS)',
        kind: 'nps',
        hint: 'How likely the student is to recommend the course, 0 to 10. 9-10 is a promoter, 7-8 passive, 0-6 a detractor.'
      }
    ]
  },
  {
    key: 'instructor',
    title: 'Instructor',
    score: 'instructorScore',
    questions: [
      { field: 'instructorExpertise', label: 'Knew the subject', kind: 'rating' },
      { field: 'instructorPrepared', label: 'Was prepared', kind: 'rating' },
      { field: 'instructorClarity', label: 'Explained clearly', kind: 'rating' },
      { field: 'instructorAnswers', label: 'Answered questions', kind: 'rating' },
      { field: 'instructorManage', label: 'Managed the classroom', kind: 'rating', hint: 'Asked of a classroom delivery. Blank on a virtual class, where the next question is asked instead.' },
      { field: 'instructorWebManage', label: 'Managed the virtual classroom', kind: 'rating', hint: 'The same question for a virtual delivery. Blank on a classroom class.' }
    ]
  },
  {
    key: 'curriculum',
    title: 'Curriculum',
    score: 'curriculumScore',
    questions: [
      { field: 'coursePace', label: 'Pace of the course', kind: 'rating' },
      { field: 'courseRelevant', label: 'Relevant to my work', kind: 'rating' },
      { field: 'studentGuideAccurate', label: 'Student guide was accurate', kind: 'rating' },
      { field: 'courseWebApp', label: 'Course web application', kind: 'rating' },
      { field: 'videosWorthwhile', label: 'Videos were worthwhile', kind: 'rating' },
      { field: 'videoQuality', label: 'Video quality', kind: 'rating' },
      { field: 'bookshelfExperience', label: 'Bookshelf experience', kind: 'rating' }
    ]
  },
  {
    key: 'labs',
    title: 'Labs',
    score: 'labScore',
    questions: [
      { field: 'labsRelevant', label: 'Labs were relevant', kind: 'rating' },
      { field: 'labPerformance', label: 'Lab performance', kind: 'rating' }
    ]
  },
  {
    key: 'facility',
    title: 'Facility',
    score: 'facilityScore',
    questions: [
      { field: 'roomConfortable', label: 'Room was comfortable', kind: 'rating' },
      { field: 'hardware', label: 'Hardware', kind: 'rating' },
      { field: 'facilityStaff', label: 'Facility staff', kind: 'rating' },
      { field: 'conferenceAudio', label: 'Conference audio', kind: 'rating' },
      { field: 'conferenceWebApp', label: 'Conference web application', kind: 'rating' }
    ]
  },
  {
    key: 'support',
    title: 'Support',
    questions: [
      { field: 'supportPre', label: 'Support before the class', kind: 'rating' },
      { field: 'supportResponsive', label: 'Support was responsive', kind: 'rating' },
      { field: 'didContactSupport', label: 'Contacted support', kind: 'yesno' },
      { field: 'increaseSupportKnowledge', label: 'Support increased their knowledge', kind: 'yesno' }
    ]
  },
  {
    key: 'preparation',
    title: 'Preparation',
    questions: [
      { field: 'didSkillsAssessment', label: 'Took the skills assessment', kind: 'yesno' },
      { field: 'metPreRequisites', label: 'Met the prerequisites', kind: 'yesno' },
      { field: 'betterPrepared', label: 'Could have been better prepared', kind: 'yesno' },
      { field: 'testedConnection', label: 'Tested their connection', kind: 'yesno' },
      { field: 'receivedLogin', label: 'Received their login in time', kind: 'yesno' }
    ]
  },
  {
    key: 'outcome',
    title: 'Outcome',
    questions: [
      { field: 'exploreAdopt', label: 'Will explore or adopt the product', kind: 'yesno' },
      { field: 'projectSucceed', label: 'Their project will succeed', kind: 'yesno' },
      { field: 'recommendExpand', label: 'Would recommend expanding', kind: 'yesno' },
      { field: 'recommendPurchase', label: 'Would recommend purchasing', kind: 'yesno' },
      { field: 'recommendRenewal', label: 'Would recommend renewing', kind: 'yesno' }
    ]
  }
];

/** Every question in the order the sections list them. */
export const SURVEY_QUESTIONS: readonly SurveyQuestion[] =
  SURVEY_SECTIONS.flatMap(section => section.questions);

/**
 * The written answers that are not one of the five comment boxes.
 *
 * Kept apart from {@link SURVEY_SECTIONS} because they cannot be scored, averaged or drawn as a
 * cell - they only ever appear in full, on one respondent's detail.
 */
export const SURVEY_TEXT_QUESTIONS: readonly { field: keyof Submission; label: string }[] = [
  { field: 'whyHere', label: 'Why they took the course' },
  { field: 'howLongPreviousCourse', label: 'How long since their previous course' },
  { field: 'whyContactSupport', label: 'Why they contacted support' },
  { field: 'expectTrainingNeeds', label: 'Expected training needs' },
  { field: 'expectTrainingConsume', label: 'How they expect to consume training' }
];

/**
 * The five comment boxes: the group on {@link SurveyComments}, the field on a {@link Submission},
 * and what to call it.
 *
 * One list for both so the anonymised view and a respondent's own detail cannot drift into naming
 * the same box two different things.
 */
export const SURVEY_COMMENT_GROUPS: readonly {
  group: keyof SurveyComments;
  field: keyof Submission;
  englishField: keyof Submission;
  label: string;
}[] = [
  { group: 'overall', field: 'commentOverall', englishField: 'commentOverallEnglish', label: 'Overall' },
  { group: 'instructor', field: 'commentInstructor', englishField: 'commentInstructorEnglish', label: 'Instructor' },
  { group: 'curriculum', field: 'commentCurriculum', englishField: 'commentCurriculumEnglish', label: 'Curriculum' },
  { group: 'facility', field: 'commentFacilities', englishField: 'commentFacilitiesEnglish', label: 'Facility' },
  {
    group: 'bookshelf',
    field: 'commentFeaturesBookshelf',
    englishField: 'commentFeaturesBookshelfEnglish',
    label: 'Features and bookshelf'
  }
];

/** The six scores, in the order the tiles show them. */
export const SURVEY_SCORES: readonly { field: keyof SurveyView; label: string; kind: QuestionKind }[] = [
  { field: 'overallScore', label: 'Overall', kind: 'rating' },
  { field: 'instructorScore', label: 'Instructor', kind: 'rating' },
  { field: 'curriculumScore', label: 'Curriculum', kind: 'rating' },
  { field: 'labScore', label: 'Labs', kind: 'rating' },
  { field: 'facilityScore', label: 'Facility', kind: 'rating' },
  { field: 'nps', label: 'NPS', kind: 'nps' }
];

// ---------------------------------------------------------------------------------------------
// Reading the comments
// ---------------------------------------------------------------------------------------------

/*
 * Everything below decides the same question three ways - is there a translation, is there
 * nothing to translate, is there work for the translation to do - and all three are answered by
 * comparing `original` with `english` rather than by a flag from the service. Trimmed on both
 * sides, because a stored translation that differs from the original only by trailing whitespace
 * is not a second version to offer a reader.
 */

const text = (value?: string | null): string => value?.trim() ?? '';

/**
 * True when the service holds English that says something other than the original: the only case
 * where a reader has two versions to switch between, and so the only case where the switch is
 * offered at all.
 */
export const hasTranslation = (comment: TranslatedComment): boolean => {
  const english = text(comment.english);
  return english.length > 0 && english !== text(comment.original);
};

/**
 * True when the student already wrote in English - the service stored the original back as its own
 * translation. There is no second version, so nothing is offered and nothing would be gained by
 * translating it again.
 */
export const isAlreadyEnglish = (comment: TranslatedComment): boolean =>
  text(comment.english).length > 0 && text(comment.english) === text(comment.original);

/**
 * True when translating this class would do something to this comment: it has been written, and it
 * is not already English.
 *
 * Deliberately true for a comment that *has* been translated - running it again is what the force
 * option is for - so this answers "is there anything here to act on" rather than "is anything
 * missing".
 */
export const isTranslatable = (comment: TranslatedComment): boolean =>
  text(comment.original).length > 0 && !isAlreadyEnglish(comment);

/** True once the translation has been run over this comment, whatever it decided. */
export const isTranslated = (comment: TranslatedComment): boolean =>
  text(comment.english).length > 0;

/** One box off a submission, as the pair the rest of this file works in. */
export const submissionComment = (
  submission: Submission,
  group: { field: keyof Submission; englishField: keyof Submission }
): TranslatedComment => ({
  original: (submission[group.field] as string | undefined) ?? '',
  english: submission[group.englishField] as string | undefined
});

/**
 * Every box on every submission that was written in, as pairs.
 *
 * What the survey page's "Translate comments" option reads to decide whether it has anything to do
 * and whether it would be redoing work - see {@link isTranslatable} and {@link isTranslated}.
 */
export const submissionComments = (submissions: Submission[]): TranslatedComment[] =>
  submissions.flatMap(submission =>
    SURVEY_COMMENT_GROUPS
      .map(group => submissionComment(submission, group))
      .filter(comment => text(comment.original).length > 0)
  );

// ---------------------------------------------------------------------------------------------
// Reading the answers
// ---------------------------------------------------------------------------------------------

/** The top of a kind's scale. A rating runs 1-5 and a Net Promoter answer 0-10. */
export const ratingScale = (kind: QuestionKind): number => (kind === 'nps' ? 10 : 5);

/** The bottom of it. Only NPS has a zero. */
export const ratingFloor = (kind: QuestionKind): number => (kind === 'nps' ? 0 : 1);

/**
 * How an answer reads, which is what colours it.
 *
 * Five buckets for a rating and three for a Net Promoter answer, because that is how each is
 * actually read: a 3 out of 5 is middling, while a 7 out of 10 on NPS is a *passive* - the standard
 * promoter (9-10) / passive (7-8) / detractor (0-6) split, which is also how upstream's own reports
 * group them. Mapping NPS onto the rating scale by proportion would paint a detractor's 6 as
 * "fair", which is the one reading of it that is wrong.
 */
export type AnswerTone = 'excellent' | 'good' | 'fair' | 'poor' | 'bad';

export const answerTone = (value: number, kind: QuestionKind): AnswerTone => {
  if (kind === 'nps') {
    if (value >= 9) return 'excellent';
    if (value >= 7) return 'fair';
    return 'bad';
  }
  if (value >= 5) return 'excellent';
  if (value >= 4) return 'good';
  if (value >= 3) return 'fair';
  if (value >= 2) return 'poor';
  return 'bad';
};

/**
 * The tone of an *average*, which is a fraction and so cannot be bucketed by equality.
 *
 * Rounds to the nearer whole answer first, so a 4.5 average reads as the 5 it is nearer to. A class
 * averaging 3.9 on a question is a "good" class on it, not a "fair" one, and the half point is the
 * only place the two disagree.
 */
export const averageTone = (average: number, kind: QuestionKind): AnswerTone =>
  answerTone(Math.round(average), kind);

/** Whether a submission answered a question at all. A skipped question is null, not zero. */
export const isAnswered = (submission: Submission, question: SurveyQuestion): boolean => {
  const value = submission[question.field];
  return value !== null && value !== undefined && value !== '';
};

/** What one submission answered, or undefined where it was skipped. */
export const numericAnswer = (submission: Submission, question: SurveyQuestion): number | undefined => {
  if (question.kind === 'yesno') return undefined;
  const value = submission[question.field];
  return typeof value === 'number' ? value : undefined;
};

/** The same for a yes/no question. */
export const booleanAnswer = (submission: Submission, question: SurveyQuestion): boolean | undefined => {
  if (question.kind !== 'yesno') return undefined;
  const value = submission[question.field];
  return typeof value === 'boolean' ? value : undefined;
};

/**
 * What a question was answered across a set of submissions.
 *
 * `count` is the number who answered it, not the number of submissions: a question two students out
 * of eight skipped has an average over six, and showing it as an average over eight would quietly
 * treat a skip as a zero. Both numbers are reported so the page can say "4.2 (6)".
 */
export interface QuestionSummary {
  question: SurveyQuestion;
  /** How many submissions answered it. */
  count: number;
  /** How many were asked - the size of the set, answered or not. */
  asked: number;
  /** The mean of the answers, or undefined when nobody answered. Ratings and NPS only. */
  average?: number;
  /** How many gave each value, keyed by the value. Ratings and NPS only. */
  distribution: Map<number, number>;
  /** Yes/no only. */
  yes: number;
  no: number;
}

export const summarise = (question: SurveyQuestion, submissions: readonly Submission[]): QuestionSummary => {
  const summary: QuestionSummary = {
    question,
    count: 0,
    asked: submissions.length,
    distribution: new Map(),
    yes: 0,
    no: 0
  };

  let total = 0;
  for (const submission of submissions) {
    if (question.kind === 'yesno') {
      const answer = booleanAnswer(submission, question);
      if (answer === undefined) continue;
      summary.count += 1;
      if (answer) summary.yes += 1;
      else summary.no += 1;
      continue;
    }

    const answer = numericAnswer(submission, question);
    if (answer === undefined) continue;
    summary.count += 1;
    total += answer;
    summary.distribution.set(answer, (summary.distribution.get(answer) ?? 0) + 1);
  }

  if (summary.count > 0 && question.kind !== 'yesno') summary.average = total / summary.count;
  return summary;
};

/**
 * The mean of a section's rating questions, over the submissions on hand.
 *
 * **Not the section's score.** {@link SurveyView.instructorScore} and the rest are upstream's, and
 * this is what the answers this service holds happen to average to; the two are computed
 * differently and are expected to differ. Anywhere both are shown, this one is labelled as computed.
 *
 * Averages the answers rather than the per-question averages, so a question most of the class
 * skipped does not weigh as much as one they all answered. Yes/no questions are left out - there is
 * no arithmetic that puts a "yes" on the same scale as a 4.
 */
export const sectionAverage = (
  section: SurveySection,
  submissions: readonly Submission[]
): number | undefined => {
  let total = 0;
  let count = 0;
  for (const question of section.questions) {
    if (question.kind !== 'rating') continue;
    for (const submission of submissions) {
      const answer = numericAnswer(submission, question);
      if (answer === undefined) continue;
      total += answer;
      count += 1;
    }
  }
  return count > 0 ? total / count : undefined;
};

/**
 * How completely a submission was filled in, as a fraction of the questions this application knows
 * about.
 *
 * Computed rather than read from {@link Submission.completionRatio}, which is upstream's own figure
 * over upstream's own form - a form that asks things this service does not store. Both are shown on
 * a respondent's row, and they are not the same measurement.
 */
export const answeredRatio = (submission: Submission): number => {
  const answered = SURVEY_QUESTIONS.filter(question => isAnswered(submission, question)).length;
  return answered / SURVEY_QUESTIONS.length;
};

/**
 * How the reader should refer to a respondent when the reader is not allowed to see who they are -
 * and, for an administrator, the short handle a matrix column is headed with.
 *
 * Positional: respondent 1 is the first submission in the order the service returned them, which is
 * by id, which is the order they were imported. Stable for a given page load and meaningless
 * outside it - two different classes' "R1" have nothing to do with each other.
 */
export const respondentHandle = (index: number): string => `R${index + 1}`;

/** How a respondent is named where there is room for a name: the name, or the handle. */
export const respondentLabel = (submission: Submission, index: number): string =>
  submission.name?.trim() || submission.email?.trim() || respondentHandle(index);

/**
 * A rating as it is written: one decimal, and nothing at all where there is no answer.
 *
 * An em dash rather than "0" or "n/a": a question nobody answered has no average, and a zero would
 * read as the worst possible score on a scale whose worst is 1.
 */
export const formatScore = (value?: number, digits = 1): string =>
  value === undefined || value === null ? '—' : value.toFixed(digits);

/** A Net Promoter Score, which is a whole number and carries a sign when it is negative. */
export const formatNps = (value?: number): string =>
  value === undefined || value === null ? '—' : Math.round(value).toString();

/** Formats a score for whichever scale it is on. */
export const formatByKind = (value: number | undefined, kind: QuestionKind): string =>
  kind === 'nps' ? formatNps(value) : formatScore(value);

/** The three groups a Net Promoter answer falls in. */
export type NpsGroup = 'promoter' | 'passive' | 'detractor';

export const npsGroup = (value: number): NpsGroup =>
  value >= 9 ? 'promoter' : value >= 7 ? 'passive' : 'detractor';

/**
 * The class's Net Promoter Score computed from the answers on hand, as a percentage of promoters
 * less detractors.
 *
 * For comparison with {@link SurveyView.nps}, which is upstream's and is the one to quote. They can
 * differ for the same reason every computed figure here can: this service is not promised every
 * submission upstream has.
 */
export const computedNps = (submissions: readonly Submission[]): number | undefined => {
  const answers = submissions
    .map(submission => submission.nps)
    .filter((value): value is number => typeof value === 'number');
  if (answers.length === 0) return undefined;

  const promoters = answers.filter(value => npsGroup(value) === 'promoter').length;
  const detractors = answers.filter(value => npsGroup(value) === 'detractor').length;
  return ((promoters - detractors) / answers.length) * 100;
};
